/**
 * LienDocsList — list + generate G702 lien waivers for a project.
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getToken } from '../../../api/client'
import {
  getLienDocs,
  getLienDocPdfUrl,
  LIEN_DOC_TYPE_LABELS,
  type LienDocRecord,
} from '../../../api/lienDocs'
import LienWaiverPanel from './LienWaiverPanel'
import UnconditionalWaiverSection from './UnconditionalWaiverSection'

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' })
}

function fmtMoney(raw: string | null | undefined): string {
  if (!raw) return '—'
  return `$${parseFloat(raw).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
}

interface Props { projectId: number; payAppId?: number }

export default function LienDocsList({ projectId, payAppId }: Props) {
  const qc = useQueryClient()
  const [showForm, setShowForm] = useState(false)

  const { data: docs = [], isPending } = useQuery({
    queryKey: ['project-lien-docs', projectId],
    queryFn: () => getLienDocs(projectId),
    staleTime: 10_000,
  })

  function refetch() {
    qc.invalidateQueries({ queryKey: ['project-lien-docs', projectId] })
    setShowForm(false)
  }

  return (
    <>
    <UnconditionalWaiverSection projectId={projectId} />
    <div style={{ marginTop: 20 }} data-testid="lien-docs-list">
      <div className="pm-section-h">
        <div>
          <div className="pm-section-tt">Lien waivers</div>
          <div className="pm-section-st">Conditional & unconditional · industry-standard forms</div>
        </div>
        <button type="button" className="btn b-dk btn-sm" data-testid="lien-generate-toggle"
          onClick={() => setShowForm(v => !v)}>
          {showForm ? 'Cancel' : '+ Generate Waiver'}
        </button>
      </div>

      {showForm && (
        <LienWaiverPanel projectId={projectId} payAppId={payAppId} onGenerated={refetch} />
      )}

      <div className="panel">
        {isPending ? (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--ink4)' }}>Loading waivers…</div>
        ) : docs.length === 0 ? (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--ink4)', textAlign: 'center' }}>
            No lien waivers generated yet.
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Type</th><th>Created</th><th>Signatory</th><th>Amount</th><th />
              </tr>
            </thead>
            <tbody>
              {docs.map((doc: LienDocRecord) => (
                <tr key={doc.id}>
                  <td style={{ fontSize: 11 }}>{LIEN_DOC_TYPE_LABELS[doc.doc_type] ?? doc.doc_type}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{fmtDate(doc.created_at)}</td>
                  <td>{doc.signatory_name}</td>
                  <td className="mono-c">{fmtMoney(doc.amount)}</td>
                  <td>
                    <button type="button" className="btn b-ol btn-sm" data-testid={`lien-doc-pdf-${doc.id}`}
                      onClick={() => window.open(getLienDocPdfUrl(doc.id, getToken() ?? ''), '_blank')}>
                      Download PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
    </>
  )
}
